export const nephron2Module = {
  id: "nephron2",
  title: "The Nephron: Part 2",
  sections: [
    {
      type: "text",
      title: "Loop of Henle & Countercurrent Multiplication",
      content: `
        <p>The <span class="glossary-term">loop of Henle</span> creates the medullary osmotic gradient that allows the kidney to produce concentrated urine. It achieves this through <strong>countercurrent multiplication</strong>.</p>

        <div class="highlight-box">
          <p><strong>Descending thin limb</strong></p>
          <ul>
            <li>Highly <strong>permeable to water</strong>, impermeable to solutes</li>
            <li>Water leaves into the hyperosmolar medullary interstitium</li>
            <li>Tubular fluid becomes progressively <strong>concentrated</strong> (up to ~1200 mOsm at the loop tip)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Thin ascending limb</strong></p>
          <ul>
            <li><strong>Impermeable to water</strong>, some passive NaCl reabsorption</li>
            <li>Osmolality begins to fall as NaCl leaves</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Thick ascending limb (TAL)</strong></p>
          <ul>
            <li><span class="glossary-term">NKCC2</span> cotransporter (Na⁺/K⁺/2Cl⁻) actively reabsorbs solute</li>
            <li>Completely <strong>impermeable to water</strong> → fluid dilutes from 1200 to ~100 mOsm</li>
            <li>Also called the <span class="purple-term">"diluting segment"</span></li>
            <li>Reabsorbed NaCl accumulates in the medullary interstitium → builds the gradient</li>
            <li>Furosemide blocks NKCC2 → abolishes medullary gradient → no concentration possible even with ADH</li>
          </ul>
        </div>

        <p><strong>Urea recycling</strong> also contributes to the gradient: urea is concentrated in the medullary collecting duct (with ADH), diffuses into the interstitium, and re-enters the descending limb. This accounts for ~50% of the inner medullary gradient.</p>

        <div class="mnemonic-card">
          <div class="mnemonic-title">Countercurrent Summary</div>
          <div class="mnemonic-content"><strong>Descending = water out (concentrated). Ascending = solute out (diluted).</strong> The gradient built by the TAL is the engine; the descending limb equilibrates with it. Together they "multiply" a single-effect gradient into a 900 mOsm cortex-to-medulla difference.</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "What is the approximate osmolality of tubular fluid at the tip of the loop of Henle?",
      options: ["150 mOsm/kg", "300 mOsm/kg", "600 mOsm/kg", "1200 mOsm/kg"],
      correct: 3,
      explanation:
        "At the tip of the loop of Henle (the inner medulla), tubular fluid osmolality equilibrates with the hyperosmolar medullary interstitium at approximately 1200 mOsm/kg. This is achieved because the descending thin limb is highly permeable to water, which leaves into the increasingly concentrated interstitium created by NKCC2 activity in the thick ascending limb.",
    },
    {
      type: "question",
      text: "Why is the thick ascending limb of the loop of Henle called the 'diluting segment'?",
      options: [
        "It adds water to the tubular lumen from the interstitium",
        "It actively reabsorbs solute via NKCC2 but is impermeable to water, diluting the tubular fluid",
        "It secretes dilute fluid from the peritubular capillaries",
        "It reabsorbs both Na⁺ and water equally, maintaining isosmolarity",
      ],
      correct: 1,
      explanation:
        "The TAL actively reabsorbs Na⁺, K⁺, and Cl⁻ via the NKCC2 cotransporter but is completely impermeable to water. Therefore, solute leaves while water stays, diluting the tubular fluid from ~1200 mOsm (at the loop tip) to ~100 mOsm by the time it reaches the DCT. This is the basis for the kidney's ability to produce hypotonic urine.",
    },
    {
      type: "question",
      text: "A patient takes a high-dose loop diuretic. Even with high circulating ADH levels, their urine remains dilute (~300 mOsm/kg). Why?",
      options: [
        "Loop diuretics block ADH receptors in the collecting duct",
        "Loop diuretics block NKCC2, abolishing the medullary gradient needed for concentration",
        "Loop diuretics increase AQP2 degradation",
        "Loop diuretics cause osmotic diuresis that overwhelms ADH",
      ],
      correct: 1,
      explanation:
        "ADH inserts AQP2 channels into the collecting duct to allow water reabsorption. However, this only works if there is an osmotic gradient in the medullary interstitium to drive water movement. Loop diuretics block NKCC2 in the TAL, preventing solute accumulation in the medulla → medullary gradient is abolished → even with maximal ADH, water cannot move out of the collecting duct → urine remains approximately isotonic (~300 mOsm).",
    },
    {
      type: "text",
      title: "Distal Convoluted Tubule (DCT)",
      content: `
        <p>The DCT continues the dilution of tubular fluid and plays a key role in <strong>calcium, magnesium, and sodium regulation</strong> under hormonal control.</p>

        <div class="highlight-box">
          <p><strong>Early DCT</strong></p>
          <ul>
            <li><span class="glossary-term">NCC</span> (Na⁺-Cl⁻ cotransporter, apical) — reabsorbs Na⁺ and Cl⁻</li>
            <li>Impermeable to water → further dilution (fluid reaches ~50–100 mOsm)</li>
            <li>Blocked by <strong>thiazide diuretics</strong> → ↑Na⁺ delivery to collecting duct, ↑K⁺ loss</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Calcium reabsorption (PTH and vitamin D sensitive)</strong></p>
          <ul>
            <li>Ca²⁺ enters via <span class="glossary-term">TRPV5</span> channels (apical) → exits via Na⁺/Ca²⁺ exchanger and Ca²⁺-ATPase (basolateral)</li>
            <li>PTH → ↑TRPV5 activity → ↑Ca²⁺ reabsorption</li>
            <li>Thiazides → ↑Ca²⁺ reabsorption (useful in hypercalciuria and calcium oxalate nephrolithiasis)</li>
            <li>Loop diuretics → ↓Ca²⁺ reabsorption → hypercalciuria (used to treat hypercalcaemia)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Magnesium reabsorption</strong></p>
          <ul>
            <li>~10% of filtered Mg²⁺ reabsorbed here via TRPM6 channels</li>
            <li>Mutations in TRPM6 → hereditary hypomagnesaemia with secondary hypocalcaemia</li>
            <li>Thiazide diuretics → ↓TRPM6 → hypomagnesaemia (chronic use)</li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">Mnemonic: Loops vs Thiazides</div>
          <div class="mnemonic-content"><strong>"Loops Lose calcium, Thiazides Take calcium"</strong> — Loop diuretics cause hypercalciuria; thiazides cause calcium retention. This is why thiazides are used in nephrolithiasis (calcium stones) and loops are used in hypercalcaemia.</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient with recurrent calcium oxalate kidney stones is prescribed a thiazide diuretic. How does this reduce stone formation?",
      options: [
        "Thiazides increase urinary citrate, which inhibits crystal formation",
        "Thiazides block NCC in the DCT, increasing Ca²⁺ reabsorption and reducing urinary calcium",
        "Thiazides alkalinise the urine, preventing calcium oxalate precipitation",
        "Thiazides reduce urinary oxalate excretion",
      ],
      correct: 1,
      explanation:
        "By blocking NCC in the early DCT, thiazides increase intracellular Na⁺ depletion → Na⁺/Ca²⁺ exchanger on the basolateral membrane increases activity to remove Na⁺ → more Ca²⁺ enters the cell from the lumen → ↑Ca²⁺ reabsorption → hypocalciuria → less calcium available to precipitate as stones. Thiazides are first-line treatment for recurrent calcium nephrolithiasis with hypercalciuria.",
    },
    {
      type: "question",
      text: "Which diuretic class causes hypercalciuria and is used to treat hypercalcaemia?",
      options: [
        "Thiazide diuretics (DCT)",
        "Loop diuretics (TAL)",
        "Potassium-sparing diuretics (collecting duct)",
        "Carbonic anhydrase inhibitors (PCT)",
      ],
      correct: 1,
      explanation:
        "Loop diuretics (e.g., furosemide) block NKCC2 in the TAL, which is also where ~25% of filtered calcium is reabsorbed via a paracellular route driven by the lumen-positive potential created by NKCC2. Blocking NKCC2 → abolishes this potential → Ca²⁺ reabsorption falls → hypercalciuria. This is exploited in treating hypercalcaemia (with IV fluids and loop diuretics).",
    },
    {
      type: "text",
      title: "Collecting Duct",
      content: `
        <p>The collecting duct is the final site of tubular regulation, controlled by <strong>aldosterone</strong> (sodium balance) and <strong>ADH/vasopressin</strong> (water balance).</p>

        <div class="highlight-box">
          <p><strong>Principal cells (~65% of CD cells)</strong></p>
          <ul>
            <li>Apical <span class="glossary-term">ENaC</span> (epithelial Na⁺ channel) — reabsorbs Na⁺; upregulated by aldosterone</li>
            <li>Apical <span class="glossary-term">ROMK</span> (K⁺ channel) — secretes K⁺ into lumen; upregulated by aldosterone</li>
            <li>Apical <span class="glossary-term">AQP2</span> (aquaporin-2) — water channel; inserted in response to ADH (V2R → cAMP → PKA → AQP2 trafficking)</li>
            <li>Basolateral AQP3/AQP4 — constitutive water exit channels</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Type A intercalated cells</strong> — acid secretion</p>
          <ul>
            <li>Apical <strong>H⁺/K⁺-ATPase</strong> → secretes H⁺, reabsorbs K⁺</li>
            <li>Basolateral HCO₃⁻/Cl⁻ exchanger (AE1) → exports HCO₃⁻ to blood</li>
            <li>Net effect: acidifies urine, generates new HCO₃⁻</li>
            <li>Failure → <span class="purple-term">Type 1 (distal) RTA</span> — can't acidify urine below pH 5.5</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Type B intercalated cells</strong> — base secretion</p>
          <ul>
            <li>Apical pendrin (HCO₃⁻/Cl⁻ exchanger) → secretes HCO₃⁻, reabsorbs Cl⁻</li>
            <li>Active in metabolic alkalosis — excretes excess HCO₃⁻</li>
          </ul>
        </div>

        <p>The medullary collecting duct (with ADH) becomes permeable to <strong>urea</strong> (via UT-A1/UT-A3) → urea enters the medullary interstitium → contributes to the osmotic gradient → the inner medullary gradient depends substantially on this urea recycling.</p>
      `,
    },
    {
      type: "question",
      text: "ADH increases urine concentration by which mechanism in principal cells?",
      options: [
        "Activating NKCC2 to increase solute reabsorption",
        "Binding V2 receptors → cAMP → PKA → trafficking AQP2 to the apical membrane",
        "Directly opening ENaC channels to increase Na⁺ reabsorption",
        "Stimulating aldosterone release from the adrenal cortex",
      ],
      correct: 1,
      explanation:
        "ADH (vasopressin) binds V2 receptors on principal cells of the collecting duct → Gs-coupled → ↑cAMP → activates PKA → phosphorylates AQP2 vesicles → they fuse with the apical membrane → water can now cross from the lumen into the cell → exits basolaterally via constitutive AQP3/AQP4 → absorbed into the hypertonic medullary interstitium. In nephrogenic DI, V2 receptors or AQP2 are dysfunctional.",
    },
    {
      type: "question",
      text: "A patient with Type 1 (distal) RTA has persistently alkaline urine even in the context of systemic metabolic acidosis. Which cell type is dysfunctional?",
      options: [
        "Principal cells — ENaC failure",
        "Type A intercalated cells — H⁺/K⁺-ATPase failure",
        "Type B intercalated cells — pendrin overactivation",
        "PCT cells — carbonic anhydrase failure",
      ],
      correct: 1,
      explanation:
        "Type A intercalated cells in the collecting duct secrete H⁺ via H⁺/K⁺-ATPase and are responsible for acidifying urine below pH 5.5. In Type 1 RTA, these cells are dysfunctional → the kidney cannot excrete sufficient acid → metabolic acidosis accumulates → urine remains inappropriately alkaline (pH >5.5). This is in contrast to Type 2 RTA where the PCT fails to reabsorb HCO₃⁻.",
    },
    {
      type: "question",
      text: "Aldosterone increases Na⁺ reabsorption and K⁺ secretion in the collecting duct. Which two channels does it upregulate?",
      options: [
        "NKCC2 and AQP2",
        "NCC and TRPV5",
        "ENaC and ROMK",
        "NHE3 and H⁺/K⁺-ATPase",
      ],
      correct: 2,
      explanation:
        "Aldosterone binds the mineralocorticoid receptor (MR) in principal cells → increases transcription of ENaC (apical Na⁺ channel) and ROMK (apical K⁺ channel). ENaC reabsorbs Na⁺ → creates a lumen-negative potential → drives K⁺ secretion via ROMK. Net effect: Na⁺ and water retention, K⁺ loss. This is the mechanism behind hypokalaemia in hyperaldosteronism (Conn's syndrome) and hyperkalaemia in Addison's disease.",
    },
    {
      type: "text",
      title: "Inherited Tubular Channelopathies",
      content: `
        <p>Several inherited syndromes result from mutations in specific tubular transporters. Recognising the <strong>electrolyte pattern and blood pressure</strong> helps distinguish them clinically.</p>

        <div class="highlight-box">
          <p><strong>Bartter syndrome</strong> — NKCC2 or ROMK mutation (thick ascending limb)</p>
          <ul>
            <li>Mimics <strong>furosemide effect</strong>: salt wasting, hypokalaemia, metabolic alkalosis</li>
            <li>↑Renin, ↑aldosterone — but <strong>normal or low blood pressure</strong></li>
            <li>Hypercalciuria → nephrocalcinosis</li>
            <li>Presents in neonates/infants (polyhydramnios, failure to thrive)</li>
            <li>Treatment: salt + K⁺ supplements; NSAIDs (↓prostaglandins that drive renin)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Gitelman syndrome</strong> — NCC mutation (early DCT)</p>
          <ul>
            <li>Mimics <strong>thiazide effect</strong>: hypokalaemia, <span class="purple-term">hypomagnesaemia</span>, metabolic alkalosis</li>
            <li><span class="purple-term">Hypocalciuria</span> (distinguishes from Bartter which has hypercalciuria)</li>
            <li>↑Renin, ↑aldosterone — but <strong>normal or low blood pressure</strong></li>
            <li>Milder than Bartter; often presents in adolescence/adulthood with cramps, fatigue</li>
            <li>Treatment: Mg²⁺ and K⁺ supplementation</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Liddle syndrome</strong> — gain-of-function ENaC mutation (collecting duct)</p>
          <ul>
            <li>ENaC stays constitutively open → excessive Na⁺ reabsorption → <span class="purple-term">hypertension</span></li>
            <li>Hypokalaemia, metabolic alkalosis</li>
            <li><strong>↓Renin, ↓aldosterone</strong> — aldosterone is suppressed because Na⁺/BP already high</li>
            <li>Does NOT respond to spironolactone (aldosterone is already low; spiro blocks aldosterone receptor)</li>
            <li>Responds to <strong>amiloride or triamterene</strong> (direct ENaC blockers)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Pseudohypoaldosteronism type 1 (PHA1)</strong> — loss-of-function ENaC or MR</p>
          <ul>
            <li>Opposite of Liddle: Na⁺ wasting, hyperkalaemia, metabolic acidosis</li>
            <li>↑↑Aldosterone (aldosterone is elevated but cannot act)</li>
            <li>Treatment: high-salt diet; some improve with age</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient has hypokalaemia, hypomagnesaemia, metabolic alkalosis, and hypocalciuria. Blood pressure is normal. Renin and aldosterone are elevated. What is the diagnosis?",
      options: [
        "Bartter syndrome",
        "Gitelman syndrome",
        "Liddle syndrome",
        "Conn's syndrome (primary hyperaldosteronism)",
      ],
      correct: 1,
      explanation:
        "Gitelman syndrome (NCC mutation, DCT) mimics thiazide diuretic use. The key distinguishing features from Bartter syndrome are: hypomagnesaemia and hypocalciuria (vs. Bartter's hypercalciuria). Both have hypokalaemia, metabolic alkalosis, elevated renin/aldosterone, and normal/low BP. Liddle syndrome and Conn's syndrome both cause hypertension, making them less likely here.",
    },
    {
      type: "question",
      text: "A young hypertensive patient has hypokalaemia and metabolic alkalosis. Serum renin and aldosterone are both very low. Which condition does this suggest, and why doesn't spironolactone help?",
      options: [
        "Conn's syndrome — spironolactone doesn't reach the adrenal gland",
        "Liddle syndrome — ENaC is constitutively active; spironolactone blocks aldosterone receptor but aldosterone is already suppressed",
        "Bartter syndrome — spironolactone worsens potassium loss",
        "Gitelman syndrome — spironolactone has no effect on the DCT",
      ],
      correct: 1,
      explanation:
        "Liddle syndrome is a gain-of-function mutation in ENaC's β or γ subunit. ENaC stays open constitutively → excessive Na⁺ reabsorption → hypertension, hypokalaemia, metabolic alkalosis. Because the body is Na⁺-replete and hypertensive, renin and aldosterone are appropriately suppressed. Spironolactone blocks the aldosterone receptor — which is useless when aldosterone is already low. Amiloride or triamterene work by directly blocking ENaC regardless of aldosterone levels.",
    },
    {
      type: "question",
      text: "Which channelopathy is associated with hypercalciuria and nephrocalcinosis?",
      options: [
        "Gitelman syndrome (NCC mutation)",
        "Bartter syndrome (NKCC2/ROMK mutation)",
        "Liddle syndrome (ENaC gain-of-function)",
        "PHA type 1 (ENaC loss-of-function)",
      ],
      correct: 1,
      explanation:
        "Bartter syndrome affects the TAL (via NKCC2 or ROMK mutations). The TAL is responsible for ~25% of filtered Ca²⁺ reabsorption via a paracellular, voltage-dependent route. Disrupting NKCC2/ROMK → abolishes the lumen-positive potential → Ca²⁺ reabsorption fails → hypercalciuria → nephrocalcinosis. This contrasts with Gitelman syndrome (DCT, NCC mutation) which causes hypocalciuria.",
    },
    {
      type: "text",
      title: "Urine Concentration & Dilution",
      content: `
        <p>The kidney can produce urine ranging from <strong>~50 mOsm/kg</strong> (maximum dilution) to <strong>~1200 mOsm/kg</strong> (maximum concentration). This range depends on two factors:</p>
        <ol>
          <li>The <strong>medullary osmotic gradient</strong> (built by the loop of Henle — abolished by loop diuretics)</li>
          <li>The <strong>AQP2 channel density</strong> in collecting duct principal cells (controlled by ADH)</li>
        </ol>

        <div class="highlight-box">
          <p><strong>Dilute urine (low ADH)</strong></p>
          <ul>
            <li>No ADH → no AQP2 insertion → collecting duct impermeable to water</li>
            <li>Tubular fluid reaching the CD is already ~100 mOsm (diluted by the TAL)</li>
            <li>Further dilution possible → minimum ~50 mOsm</li>
            <li>Example: large water intake, central DI, nephrogenic DI</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Concentrated urine (high ADH)</strong></p>
          <ul>
            <li>ADH → AQP2 inserted → water leaves CD into hypertonic medullary interstitium</li>
            <li>Maximum concentration = medullary gradient (~1200 mOsm) when tubular flow is slow</li>
            <li>Example: hypovolaemia, SIADH</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Isosthenuria (fixed osmolality ~300 mOsm)</strong></p>
          <ul>
            <li>Urine osmolality fixed equal to plasma; kidney cannot concentrate OR dilute</li>
            <li>Hallmark of severe tubular damage or advanced CKD</li>
            <li>Medullary gradient destroyed and/or tubular cell dysfunction</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Impact of loop diuretics on concentration</strong></p>
          <ul>
            <li>NKCC2 blockade → no medullary gradient → even with maximal ADH, urine is ~isotonic (300 mOsm)</li>
            <li>This is called an <span class="purple-term">isotonic diuresis</span></li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">Urine Concentration: Two Requirements</div>
          <div class="mnemonic-content"><strong>ADH + Intact medullary gradient</strong> — both are needed to make concentrated urine. ADH is absent in DI; the gradient is absent after furosemide or in CKD. Loss of either → inability to concentrate beyond ~300 mOsm.</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient with suspected diabetes insipidus undergoes a water deprivation test. After 8 hours, urine osmolality remains <200 mOsm/kg. Desmopressin (synthetic ADH) is administered, and urine osmolality rises to 700 mOsm/kg. What type of DI does this indicate?",
      options: [
        "Nephrogenic DI — the kidney cannot respond to ADH",
        "Central (cranial) DI — the posterior pituitary fails to secrete ADH, so exogenous ADH restores concentration",
        "Primary polydipsia — excess water intake suppresses ADH",
        "SIADH — ADH is already maximal before desmopressin",
      ],
      correct: 1,
      explanation:
        "In central (cranial) DI, the posterior pituitary fails to produce or secrete ADH. The collecting duct principal cells (with intact V2 receptors and AQP2) are normal and respond appropriately to exogenous ADH (desmopressin) → AQP2 inserted → concentrated urine. In nephrogenic DI, desmopressin has no effect (V2 receptor or AQP2 dysfunction). Response to desmopressin is the key test to distinguish the two.",
    },
    {
      type: "question",
      text: "A patient with advanced CKD (stage 4) has urine osmolality of 305 mOsm/kg regardless of fluid intake. What is this called and why does it occur?",
      options: [
        "Hypersthenuria — the damaged nephrons concentrate too efficiently",
        "Isosthenuria — tubular damage and loss of medullary gradient means the kidney can neither concentrate nor dilute urine",
        "Hyposthenuria — reduced ADH production from kidney damage",
        "Free water diuresis — glomerular hyperfiltration",
      ],
      correct: 1,
      explanation:
        "Isosthenuria (urine fixed at ~300 mOsm/kg = plasma osmolality) is a hallmark of severe tubular damage. In advanced CKD, the medullary gradient is destroyed (nephron loss, interstitial scarring, vasa recta damage) and remaining tubular cells are dysfunctional. The kidney can neither concentrate urine above 300 mOsm (lack of gradient) nor dilute below 300 mOsm (residual tubular damage). Nocturia (from loss of concentrating ability) is an early symptom.",
    },
    {
      type: "question",
      text: "Which of the following statements about urine concentration is correct?",
      options: [
        "ADH alone is sufficient to produce maximally concentrated urine even after loop diuretics",
        "The minimum urine osmolality achievable by the normal kidney is approximately 50 mOsm/kg",
        "The collecting duct is always permeable to water regardless of ADH levels",
        "Urea does not contribute to the medullary osmotic gradient",
      ],
      correct: 1,
      explanation:
        "The minimum urine osmolality achievable in maximum water diuresis (no ADH) is approximately 50 mOsm/kg. The collecting duct is impermeable to water in the absence of ADH (no AQP2 insertion). ADH alone is insufficient if the medullary gradient has been abolished (e.g., by furosemide). Urea recycling (from medullary collecting duct → interstitium → descending limb) contributes significantly (~50%) to the inner medullary gradient.",
    },
  ],
};
